import { useRef, type FormEvent } from "react"
import { Head, router, useForm, usePage } from "@inertiajs/react"
import { Upload, CheckCircle2, AlertTriangle, Clock } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import type { PageProps } from "@/types/inertia"

type Refresh = {
  id: number
  period_label: string | null
  as_of: string | null
  source_filename: string | null
  status: string
  error_detail: string | null
  live: boolean
  uploaded_by: string
  created_at: string
}

const STATUS_LABEL: Record<string, string> = {
  queued: "Queued",
  processing: "Processing",
  ready: "Ready",
  failed: "Failed",
}

function StatusIcon({ status }: { status: string }) {
  if (status === "ready") return <CheckCircle2 className="h-4 w-4 text-accent-display" />
  if (status === "failed") return <AlertTriangle className="h-4 w-4 text-danger-display" />
  return <Clock className="h-4 w-4 text-ink-muted" />
}

export default function AdminDataRefreshesIndex({ refreshes }: { refreshes: Refresh[] }) {
  const { props } = usePage<PageProps>()
  const form = useForm<{ file: File | null }>({ file: null })
  const fileInput = useRef<HTMLInputElement>(null)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post("/admin/data_refreshes", {
      forceFormData: true,
      onSuccess: () => {
        form.reset()
        if (fileInput.current) fileInput.current.value = ""
      },
    })
  }

  return (
    <>
      <Head title="Data refresh">
        <meta name="description" content="Upload the monthly export to refresh every SONAR dashboard." />
        <meta property="og:title" content="Data refresh" />
        <meta property="og:description" content="Upload the monthly export to refresh every SONAR dashboard." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <h1>Data refresh</h1>
          <p className="mt-1">
            Upload the monthly export (the JSON bundle produced by the dsd-sales-report skill) to refresh every
            dashboard. The latest valid upload goes live and supersedes the previous one; until the first upload,
            the dashboards show seeded sample data.
          </p>
        </div>

        {props.flash?.notice && (
          <div className="callout callout-accent mt-6 text-sm">{props.flash.notice}</div>
        )}
        {props.flash?.alert && (
          <div className="callout callout-danger mt-6 text-sm">{props.flash.alert}</div>
        )}

        <form onSubmit={submit} className="callout mt-6">
          <label htmlFor="refresh-file">Monthly export (JSON)</label>
          <input
            id="refresh-file"
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            onChange={(e) => form.setData("file", e.target.files?.[0] ?? null)}
            className="form-control mt-2 cursor-pointer py-1.5 file:mr-3 file:rounded file:border-0 file:bg-surface file:px-3 file:py-1.5 file:text-sm file:text-ink-body"
          />
          {props.errors?.file && <p className="mt-2 text-sm text-danger-display">{props.errors.file}</p>}
          <Button type="submit" className="mt-4" disabled={form.processing || !form.data.file}>
            <Upload className="h-4 w-4" />
            Upload refresh
          </Button>
        </form>

        <h2 className="mt-10">History</h2>
        {refreshes.length === 0 ? (
          <p className="mt-2 text-ink-muted">No refreshes yet — the dashboards are showing seeded sample data.</p>
        ) : (
          <ul className="mt-4 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
            {refreshes.map((r) => (
              <li key={r.id} className="flex items-center gap-3 px-4 py-3">
                <StatusIcon status={r.status} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium text-ink-display">
                      {r.period_label || r.source_filename || `Refresh #${r.id}`}
                    </span>
                    {r.live && <Badge tone="accent">Live</Badge>}
                  </div>
                  <div className="truncate text-xs text-ink-muted">
                    {STATUS_LABEL[r.status] ?? r.status}
                    {r.error_detail ? ` — ${r.error_detail}` : ""} · {r.uploaded_by} · {formatDate(r.created_at)}
                  </div>
                </div>
                {r.status === "ready" && !r.live && (
                  <Button variant="secondary" size="sm" onClick={() => router.patch(`/admin/data_refreshes/${r.id}`)}>
                    Make live
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </AdminShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}
